// src/pages/merchant/dashboard/Subscription.jsx
import React, { useEffect, useMemo, useState } from "react";
import "./dashboard.css";
import {
  Award,
  CheckCircle,
  Star,
  Headphones,
  Clock,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchPlans,
  fetchMySubscription,
  purchaseSubscription,
} from "../../../features/user/subscriptionThunks";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { createCashPayment, checkCashPaymentStatus } from "../../../features/payment/paymentThunks";
import { clearCashPayment } from "../../../features/payment/paymentSlice";
import PaymentOptionModal from "./pyment/PaymentOption";
import PaymentQRModal from "./pyment/PaymentQRModal";
import PaymentSummaryModal from "./pyment/PymentSummary";
import useOnlinePayment from "../../../hooks/useOnlinePayment";


const Subscription = () => {
  const dispatch = useDispatch();
  const {
    plans,
    loadingPlans,
    plansError,
    mySubscription,
    loadingMySubscription,
    mySubscriptionError,
    buying,
    buyError,
    lastPurchaseMessage,
  } = useSelector((state) => state.subscription || {});

  // subsscription check
   const navigate = useNavigate();
  
  useEffect(() => {
    if (loadingMySubscription) return;
  
    if (
      mySubscription?.expiryDate &&
      mySubscription?.payment_status === "paid"
    ) {
      const expiry = new Date(mySubscription.expiryDate);
      const isActive = expiry.getTime() > Date.now();
  
      // if (isActive) {
      //   navigate("/dashboard", { replace: true });
      // }
    }
  }, [mySubscription, loadingMySubscription, navigate]);

  // selectedPlanId from backend (_id)
const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
const [summaryOpen, setSummaryOpen] = useState(false);
const [paymentSuccessful, setPaymentSuccessful] = useState(false);
const { startOnlinePayment } = useOnlinePayment();

const {
  cashPaymentData,
  loading: paymentLoading,
  payment_status,
  status,
} = useSelector((state) => state.payment);


const handlePurchase = () => {
  if (!selectedPlan?._id) {
    toast.error("Please select a plan");
    return;
  }
  setSummaryOpen(true);
};
const handleProceedToPay = () => {
  setSummaryOpen(false);
  setShowPaymentModal(true);
};

const handlePaymentSelect = async (method) => {
 if (!selectedPlan?._id) return;

  // 1️⃣ Create subscription ONCE
  const purchaseRes = await dispatch(
    purchaseSubscription({
      planId: selectedPlan._id,
      paymentMode: method, // cash | online
    })
  );

  if (!purchaseSubscription.fulfilled.match(purchaseRes)) {
    toast.error("Failed to initiate subscription");
    return;
  }

  const purchaseId = purchaseRes.payload.data._id;
  const amount = purchaseRes.payload.data.totalAmount;

  // 2️⃣ CASH PAYMENT
  if (method === "cash") {
    await dispatch(
      createCashPayment({
        bookingId: purchaseId,
        amount,
        paymentFor: "subscription",
      })
    );
  }

  // 3️⃣ ONLINE PAYMENT
  if (method === "online") {
    startOnlinePayment({
      bookingId: purchaseId,
      amount,
      paymentFor: "subscription",
      onSuccess: () => window.location.reload(),
      onFailure: toast.error,
    });
  }
};



useEffect(() => {
  if (!cashPaymentData?.orderId || paymentSuccessful) return;

  const interval = setInterval(() => {
    dispatch(checkCashPaymentStatus(cashPaymentData.orderId));
  }, 3000);

  return () => clearInterval(interval);
}, [cashPaymentData?.orderId, paymentSuccessful, dispatch]);

useEffect(() => {
  if (payment_status === "paid" && !paymentSuccessful) {
    setPaymentSuccessful(true);

    setTimeout(() => {
      window.location.reload();
    }, 1500);
  }
}, [payment_status, paymentSuccessful]);


  // Load plans + current subscription on mount
  useEffect(() => {
    dispatch(fetchPlans());
    dispatch(fetchMySubscription());
  }, [dispatch]);

  // Toast for errors / success
  useEffect(() => {
    if (plansError) toast.error(plansError);
  }, [plansError]);

  useEffect(() => {
    if (mySubscriptionError) toast.error(mySubscriptionError);
  }, [mySubscriptionError]);

  useEffect(() => {
    if (buyError) toast.error(buyError);
  }, [buyError]);

  useEffect(() => {
    if (lastPurchaseMessage) toast.success(lastPurchaseMessage);
  }, [lastPurchaseMessage]);

  // Compute active subscription info
//  activeSubInfo FIRST
const activeSubInfo = useMemo(() => {
  if (!mySubscription) return null;
  const expiry = mySubscription.expiryDate
    ? new Date(mySubscription.expiryDate)
    : null;
  let daysLeft = null;
  if (expiry) {
    const now = new Date();
    const diffMs = expiry.getTime() - now.getTime();
    daysLeft = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
  }
  return {
    title: mySubscription.planTitle || mySubscription?.plan?.title || "Active Plan",
    amount: mySubscription.planAmount,
    duration: mySubscription.planDuration,
    expiry,
    daysLeft,
  };
}, [mySubscription]);

//  hasActiveSubscription AFTER
const hasActiveSubscription =
  activeSubInfo?.daysLeft !== null && activeSubInfo?.daysLeft > 0;


  // Convert backend duration (days) to human-readable label
  const getDurationLabel = (duration) => {
    if (!duration) return "Custom Duration";
    if (duration % 365 === 0) return `${duration / 365} Year${duration / 365 > 1 ? "s" : ""}`;
    if (duration % 30 === 0) return `${duration / 30} Month${duration / 30 > 1 ? "s" : ""}`;
    if (duration === 7) return "7 Days";
    return `${duration} Days`;
  };



  useEffect(() => {
  if (plans?.length && !selectedPlan) {
    setSelectedPlan(plans[0]);
  }
}, [plans, selectedPlan]);


 const handleSelectPlan = (plan) => {
  setSelectedPlan(plan);
};




  const isLoading = loadingPlans || loadingMySubscription;
  return (
    <>
      <div className="subscription-container">
        <div className="container">
          {/* Header Section */}
          <div className="merchant-profile-header mt-2">
            <div className="profile-image">
              <Award size={32} />
            </div>
            <h1 className="profile-merchant">Business Subscription</h1>
            <div className="merchant-profile-phone">
              Choose the perfect plan for your business growth
            </div>
            <div className="merchant-profile-email" />
          </div>

          {/* Content Section */}
          <div className="content-section">
            {/* Active Subscription */}
            {activeSubInfo ? (
              <div className="active-subscription border border-success border-2">
                <div className="subscription-header">
                  <CheckCircle
                    className="check-icon text-success"
                    size={24}
                  />
                  <span className="subscription-status">
                    Active Subscription
                  </span>
                </div>

                <div className="subscription-details">
                  <div className="plan-info">
                    <h3>{activeSubInfo.title}</h3>
                    {activeSubInfo.expiry && (
                      <p className="plan-expires">
                        Expires:{" "}
                        {activeSubInfo.expiry.toLocaleDateString("en-IN")}
                      </p>
                    )}
                  </div>
                  {activeSubInfo.daysLeft !== null && (
                    <div className="days-left-badge bg-success">
                      {activeSubInfo.daysLeft} day
                      {activeSubInfo.daysLeft !== 1 ? "s" : ""} left
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="active-subscription border border-warning border-2">
                <div className="subscription-header">
                  <CheckCircle className="check-icon text-warning" size={24} />
                  <span className="subscription-status">
                    No Active Subscription
                  </span>
                </div>
                <p className="mt-2 mb-0">
                  Select a plan below to activate your business subscription.
                </p>
              </div>
            )}

            {/* Plans Section */}
            <h2 className="plans-section-title">Choose Your Business Plan</h2>
            <p className="plans-section-subtitle">
              Select a plan that best fits your business needs
            </p>

            {isLoading && (
              <p style={{ marginBottom: "1rem" }}>Loading plans...</p>
            )}

            {!isLoading && plans.length === 0 && (
              <p style={{ marginBottom: "1rem" }}>
                No subscription plans available right now.
              </p>
            )}

           <div className="plans-grid">
  {plans.map((plan) => {
    const isSelected = selectedPlan?._id === plan._id;

    return (
     <div
  key={plan._id}
  className={`plan-card ${
    isSelected ? "selected" : ""
  } ${hasActiveSubscription ? "disabled" : ""}`}
  onClick={() => {
    if (hasActiveSubscription) return;
    setSelectedPlan(plan);
  }}
>
        <div className="plan-header">
          <div>
            <h3 className="plan-name">{plan.title}</h3>
            <p className="plan-duration">
              {getDurationLabel(plan.duration)}
            </p>
          </div>

          {plan.discount && (
            <div className="discount-badge">{plan.discount}% OFF</div>
          )}
        </div>

  <del className="text-muted fs-5">
    ₹{plan.original_amount}
  </del>


        <div className="plan-pricing">
          <div className="current-price text-success">
            ₹{plan.amount.toFixed(2)}
          </div>
        </div>

        {/* static features */}
        <ul className="plan-features">
          <li><Star size={16}/> Premium listing</li>
          <li><Headphones size={16}/> Priority support</li>
          <li><Clock size={16}/> 24/7 support</li>
        </ul>
      </div>
    );
  })}
</div>


 <button
  className="select-plan-button"
  onClick={handlePurchase}
  disabled={
    hasActiveSubscription || !selectedPlan || buying || cashPaymentData
  }
>
  {hasActiveSubscription
    ? "Subscription Active"
    : cashPaymentData
    ? "Payment Pending"
    : buying
    ? "Processing..."
    : "Select Plan"}
</button>

{hasActiveSubscription && (
  <p className="text-success text-center mt-3">
    You already have an active subscription. You can renew after expiry.
  </p>
)}



{/* 👇 ADD HERE */}
{cashPaymentData && (
  <p className="text-warning text-center mt-3">
    Payment pending. Subscription will activate after verification.
  </p>
)}
          </div>
        </div>

<PaymentSummaryModal
  show={summaryOpen}
  slot={{
    amount: selectedPlan?.amount || 0,          // ✅ BASE AMOUNT
    gstPercentage: selectedPlan?.gstPercentage || 18,
    gstAmount: selectedPlan?.gstAmount || 0,
    totalAmount: selectedPlan?.totalAmount || 0,
  }}
  onClose={() => setSummaryOpen(false)}
  onBookClick={handleProceedToPay}
/>

 <PaymentOptionModal
  open={showPaymentModal}
  onClose={() => setShowPaymentModal(false)}
  onSelect={handlePaymentSelect}
/>

<PaymentQRModal
  open={Boolean(cashPaymentData)}
  data={cashPaymentData}
  status={status}
  payment_status={payment_status}
  onClose={() => dispatch(clearCashPayment())}
/>


      </div>
    </>
  );
};

export default Subscription;
